import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportFilters from './components/ReportFilters';
import ReportPreview from './components/ReportPreview';
import ScheduleReportModal from './components/ScheduleReportModal';
import ScheduledReportsList from './components/ScheduledReportsList';
import { fetchReportData, generatePDFReport, generateCSVReport } from '../../services/reportService';
import { generateScoutingInsights } from '../../services/aiAnalysisService';
import { useAuth } from '../../contexts/AuthContext';

const PerformanceReports = () => {
  const { userProfile, user } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');
  const [reportType, setReportType] = useState('players');
  const [filters, setFilters] = useState({
    position: 'all',
    league: 'all',
    timeframe: '30days'
  });
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [scoutingInsights, setScoutingInsights] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const result = await fetchReportData(reportType, filters, user, userProfile);
      
      // Role-based filtering: scouts and clubs see all data, players/coaches see only their own
      let filteredData = result?.data || [];
      
      if (userProfile?.role === 'player' && reportType === 'players') {
        // Players only see their own player records
        filteredData = filteredData?.filter(record => record?.userId === userProfile?.id);
      } else if (userProfile?.role === 'coach' && reportType === 'coaches') {
        // Coaches only see their own coach records
        filteredData = filteredData?.filter(record => record?.userId === userProfile?.id);
      }
      // Scouts and clubs see all records (no filtering)
      
      setReportData(filteredData);
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScoutingInsights = async () => {
    setLoadingInsights(true);
    setInsightsError(null);
    try {
      const allPlayers = await fetchReportData('players', {}, user, userProfile);
      const allCoaches = await fetchReportData('coaches', {}, user, userProfile);
      const allGoalkeepers = await fetchReportData('goalkeepers', {}, user, userProfile);
      
      const result = await generateScoutingInsights({
        players: allPlayers?.data || [],
        coaches: allCoaches?.data || [],
        goalkeepers: allGoalkeepers?.data || []
      });
      
      if (result?.error) {
        setInsightsError(result?.error);
      } else {
        setScoutingInsights(result?.insights || []);
      }
    } catch (err) {
      setInsightsError('Failed to generate scouting insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      position: 'all',
      league: 'all',
      timeframe: '30days'
    });
  };

  const handleExportPDF = async () => {
    try {
      await generatePDFReport(reportData, reportType, filters);
    } catch (error) {
      console.error('PDF export error:', error);
    }
  };

  const handleExportCSV = () => {
    try {
      generateCSVReport(reportData, reportType);
    } catch (error) {
      console.error('CSV export error:', error);
    }
  };

  const tabs = [
    { id: 'generate', label: 'Generate Report', icon: 'FileText' },
    { id: 'scheduled', label: 'Scheduled Reports', icon: 'Clock' }
  ];

  const reportTypes = [
    { value: 'players', label: 'Players', icon: 'User' },
    { value: 'coaches', label: 'Coaches', icon: 'Clipboard' },
    { value: 'goalkeepers', label: 'Goalkeepers', icon: 'Shield' }
  ];

  return (
    <>
      <Helmet>
        <title>Performance Reports | ProX11 Market</title>
        <meta name="description" content="Generate custom performance reports with data export to PDF/CSV, filtering by position/league/timeframe, and scheduled email delivery" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/10 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                <Icon name="FileText" size={20} className="text-primary" />
                <span className="text-sm font-medium text-primary">Custom Reports</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Performance Reports
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Generate custom performance reports with advanced filtering, data export to PDF/CSV, and scheduled email delivery for stakeholders
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
              </button>
            ))}
          </div>

          {activeTab === 'generate' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Filters */}
              <div className="lg:col-span-1 space-y-6">
                {/* Report Type Selection */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Report Type</h3>
                  <div className="space-y-2">
                    {reportTypes?.map((type) => (
                      <button
                        key={type?.value}
                        onClick={() => setReportType(type?.value)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${
                          reportType === type?.value
                            ? 'border-primary bg-primary/5 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
                        }`}
                      >
                        <Icon name={type?.icon} size={20} />
                        <span className="font-medium">{type?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filters */}
                <ReportFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                  reportType={reportType}
                />

                {/* Export Actions */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-bold text-foreground mb-4">Export Options</h3>
                  <div className="space-y-3">
                    <Button
                      fullWidth
                      variant="default"
                      iconName="FileDown"
                      iconPosition="left"
                      onClick={handleExportPDF}
                      disabled={reportData?.length === 0}
                    >
                      Export as PDF
                    </Button>
                    <Button
                      fullWidth
                      variant="outline"
                      iconName="Table"
                      iconPosition="left"
                      onClick={handleExportCSV}
                      disabled={reportData?.length === 0}
                    >
                      Export as CSV
                    </Button>
                    <Button
                      fullWidth
                      variant="secondary"
                      iconName="Mail"
                      iconPosition="left"
                      onClick={() => setShowScheduleModal(true)}
                      disabled={reportData?.length === 0}
                    >
                      Schedule Email Delivery
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="lg:col-span-2">
                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg border border-border p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Sparkles" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">AI Scouting Insights</h3>
                        <p className="text-sm text-muted-foreground">Strategic recommendations from AI analysis</p>
                      </div>
                    </div>
                    <Button
                      onClick={fetchScoutingInsights}
                      variant="primary"
                      size="sm"
                      disabled={loadingInsights}
                    >
                      <Icon name="Brain" size={16} />
                      {scoutingInsights?.length > 0 ? 'Refresh' : 'Generate'} Insights
                    </Button>
                  </div>

                  {insightsError && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 text-red-600">
                        <Icon name="AlertCircle" size={20} />
                        <p className="text-sm font-medium">{insightsError}</p>
                      </div>
                    </div>
                  )}

                  {loadingInsights ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center space-y-3">
                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                        <p className="text-sm text-muted-foreground">Generating scouting insights...</p>
                      </div>
                    </div>
                  ) : scoutingInsights?.length > 0 ? (
                    <div className="space-y-4">
                      {scoutingInsights?.map((insight, index) => {
                        const priorityColors = {
                          high: 'bg-red-500/10 text-red-600 border-red-500/20',
                          medium: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
                          low: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                        };
                        const priorityColor = priorityColors?.[insight?.priority?.toLowerCase()] || priorityColors?.medium;

                        return (
                          <div key={index} className="bg-card rounded-lg border border-border p-5">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase">{insight?.category}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${priorityColor}`}>
                                    {insight?.priority} Priority
                                  </span>
                                </div>
                                <h4 className="text-lg font-bold text-foreground">{insight?.title}</h4>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">{insight?.description}</p>
                            {insight?.actionItems?.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">Action Items:</p>
                                <ul className="space-y-1">
                                  {insight?.actionItems?.map((action, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                                      <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                                      <span>{action}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Icon name="Lightbulb" size={32} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">Click "Generate Insights" to get AI-powered scouting recommendations</p>
                    </div>
                  )}
                </div>

                <ReportPreview
                  data={reportData}
                  reportType={reportType}
                  filters={filters}
                  loading={loading}
                />
              </div>
            </div>
          ) : (
            <ScheduledReportsList />
          )}
        </div>
      </div>
      {/* Schedule Report Modal */}
      {showScheduleModal && (
        <ScheduleReportModal
          reportType={reportType}
          filters={filters}
          reportData={reportData}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => {
            setShowScheduleModal(false);
            setActiveTab('scheduled');
          }}
        />
      )}
    </>
  );
};

export default PerformanceReports;